import { TimelineItemStatus, TimelineStatus } from '@components'

import { SkeletonBox } from '@billy/design-system'
import { ReactElement, useMemo } from 'react'

import { InvoiceEvent } from '../../types/invoices/invoiceEvent'
import { QuoteEvent } from '../../types/quotes/quoteEvent'

const SKELETON_BOX_WIDTH = 218
const SKELETON_BOX_HEIGHT = 120
const MIN_NUMBER_OF_VISIBLE_EVENTS = 3

type DocumentEvent = QuoteEvent | InvoiceEvent

interface DocumentTimelineStatusProps<T extends DocumentEvent> {
  events: T[]
  getItemDescription?: (event: T) => string | undefined
  getItemStatus?: (event: T) => TimelineItemStatus
  getItemTitle: (event: T) => string
  isLoading: boolean
  maxHeight?: number
  onCollapseChange?: () => void
  title: string
}

export const DocumentTimelineStatus = <T extends DocumentEvent>({
  events,
  getItemDescription,
  getItemStatus,
  getItemTitle,
  isLoading,
  maxHeight,
  onCollapseChange,
  title,
}: DocumentTimelineStatusProps<T>): ReactElement => {
  const hasMultipleTimelineStatusEvents = useMemo(() => events.length > MIN_NUMBER_OF_VISIBLE_EVENTS, [events])

  if (isLoading) {
    return <SkeletonBox width={SKELETON_BOX_WIDTH} height={SKELETON_BOX_HEIGHT} />
  }

  return (
    <TimelineStatus
      isCollapsed={true}
      isCollapsible={hasMultipleTimelineStatusEvents}
      maxHeight={maxHeight}
      minVisibleItemsCount={MIN_NUMBER_OF_VISIBLE_EVENTS}
      onCollapseChange={onCollapseChange}
      title={title}
    >
      <TimelineStatus.ItemsList>
        {events.map((event) => (
          <TimelineStatus.Item
            date={event.createdAt}
            description={getItemDescription?.(event)}
            key={event.createdAt}
            status={getItemStatus?.(event) ?? 'neutral'}
            title={getItemTitle(event)}
          />
        ))}
      </TimelineStatus.ItemsList>
    </TimelineStatus>
  )
}
